/**
 * Route-chunk prefetch warmers — one per lazily-loaded route.
 *
 * Each is `() => import(/* webpackChunkName: "…" *\/ './Route')` using the SAME
 * chunk name as the corresponding `React.lazy(...)` in App.js, so the bundler
 * emits ONE chunk that the warmer preloads and the on-navigate lazy import then
 * gets from cache (the `codeSplitting.test.js` "same chunk names" guard locks
 * this in). Preloading is the "everything feels instant" contract.
 *
 * WHY ITS OWN MODULE (not inside App.js): both App.js (`handleLogin` warms the
 * student/teacher subset right after auth) AND `components/NavBar.js` (warms on
 * link hover/focus) consume these. Housing them here — a leaf both import —
 * keeps the module graph ACYCLIC. Declaring them in App.js and importing them
 * back into NavBar (which App.js renders) would create an App↔NavBar import
 * cycle; benign only as long as every call stays at event-time, a footgun a
 * shared leaf removes outright.
 */
// RichText's own 625 kB chunk (react-markdown + KaTeX + rehype/remark +
// dompurify) is now lazy behind `components/ui/LazyRichText` (arch Wave 2.1) —
// it no longer rides in as a static dependency of every route chunk. That is
// the win for markdown-LIGHT pages (a dashboard needn't pay for KaTeX). But
// the markdown-GUARANTEED surfaces (Chat thread, the ExerciseView question,
// Learn, Progress) must still feel instant, so we WARM the RichText chunk in
// the same post-login fan-out that warms their route chunks. Same import
// specifier + chunk-name as LazyRichText's `React.lazy(() => import('./…/
// RichText'))`, so the warmer and the on-mount import dedupe to ONE chunk
// (the codeSplitting.test guard locks the name-match in, exactly as it does
// for the route warmers). Net effect: signed-in students still get instant
// math; only the paths that never render markdown stop downloading it.
export const _prefetchRichText = () =>
  import(/* webpackChunkName: "rich-text" */ './components/ui/RichText');
export const _prefetchChat = () => import(/* webpackChunkName: "route-chat" */ './Chat');
export const _prefetchProgress = () => import(/* webpackChunkName: "route-progress" */ './Progress');
export const _prefetchLearningPaths = () => import(/* webpackChunkName: "route-paths" */ './LearningPaths');
export const _prefetchExerciseLayout = () => import(/* webpackChunkName: "route-exercise" */ './ExerciseLayout');
// F11 PR2: the Daily Review session runner. Not in the post-login fan-out
// (App.js `handleLogin`) — like Prep/Career/Advisor, it's a secondary surface
// warmed on demand rather than for every login, matching the existing
// precedent that not every route is worth the bytes on every sign-in. Hover/
// focus-warmed instead from wherever the "Start review" CTA renders
// (components/ReviewCTA.js), mirroring NavBar's own hover-prefetch idiom.
export const _prefetchReviewSession = () => import(/* webpackChunkName: "route-review" */ './ReviewSession');
export const _prefetchClassrooms = () => import(/* webpackChunkName: "route-classrooms" */ './Classrooms');
export const _prefetchPrep = () => import(/* webpackChunkName: "route-prep" */ './Prep');
export const _prefetchAdvisor = () => import(/* webpackChunkName: "route-advisor" */ './admissions/AdvisorSection');
export const _prefetchCareer = () => import(/* webpackChunkName: "route-career" */ './career/CareerSection');
export const _prefetchCareerSignoff = () => import(/* webpackChunkName: "route-career-signoff" */ './career/TeacherCareerSignoff');
export const _prefetchGuardianHome = () => import(/* webpackChunkName: "route-guardian" */ './guardian/GuardianHome');
export const _prefetchFounderDashboard = () => import(/* webpackChunkName: "route-founder-dashboard" */ './pages/FounderDashboard');
